import React, { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserTypes } from '../../../types';
import { Team } from '../../../types/teams.types';
import { Group } from '../../../types/groups.types';
import { Tag } from '../../../types/tags.types';
import { UnifiedFilterBar, FilterField } from '../../shared/UnifiedFilterBar';
import { displayName } from '../../../helpers/displayName';

interface Props {
    onFilter(filters: UserTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
    allTeams: Team[];
    allGroups: Group[];
    allTags: Tag[];
}

const primaryFilters: FilterField[] = [
    { key: 'name', label: 'Name', type: 'text', placeholder: 'Search by name...' },
    { key: 'phoneNumber', label: 'Phone Number', type: 'text', placeholder: 'Search by phone...' },
    { key: 'employeeId', label: 'Employee ID', type: 'text', placeholder: 'Search by ID...' },
];

const VALID_ACTIVITY: UserTypes.ActivityWindow[] = [
    'today', '7days', '30days',
    'inactive-today', 'inactive-7days', 'inactive-30days',
];

const ACTIVITY_LABELS: Record<string, string> = {
    'today': 'Active Today',
    '7days': 'Active Last 7 Days',
    '30days': 'Active Last 30 Days',
    'inactive-today': 'Inactive Today',
    'inactive-7days': 'Inactive Last 7 Days',
    'inactive-30days': 'Inactive Last 30 Days',
};

type Values = {
    name: string;
    phoneNumber: string;
    employeeId: string;
    activity: string;
    groupIds: string[];
    teamIds: string[];
    tags: string[];
};

const buildEmpty = (): Values => ({
    name: '', phoneNumber: '', employeeId: '', activity: '', groupIds: [], teamIds: [], tags: [],
});

export const UserListFilters: React.FC<Props> = ({ onFilter, onDownload, allTeams, allGroups, allTags }) => {
    const [searchParams] = useSearchParams();
    const urlActivity = searchParams.get('activity') || '';
    const initialActivity = (VALID_ACTIVITY as string[]).includes(urlActivity) ? urlActivity : '';

    // Mirrors system-users-filters: `values` = current input state; `applied`
    // = snapshot of the last Apply. Pills render from `applied` so they don't
    // flicker mid-keystroke.
    const buildInitial = (): Values => ({ ...buildEmpty(), activity: initialActivity });
    const [values, setValues] = useState<Values>(buildInitial);
    const [applied, setApplied] = useState<Values>(buildInitial);

    // When groups are selected, narrow the visible team list to only those
    // teams that belong to the selected group(s). When no groups are selected,
    // all teams are shown. This makes the Team dropdown context-aware and also
    // makes the backend intersection semantics feel natural to the user.
    const visibleTeams = useMemo(() => {
        if (values.groupIds.length === 0) return allTeams;
        const groupSet = new Set(values.groupIds);
        return allTeams.filter((t) => t.groupId && groupSet.has(t.groupId));
    }, [allTeams, values.groupIds]);

    // Map team id → its parent group's name so the Team filter dropdown can
    // be sectioned (and Molina-style duplicate team names disambiguated).
    const groupNameByTeamId = useMemo(() => {
        const m = new Map<string, string>();
        const groupNameById = new Map<string, string>();
        for (const g of allGroups) groupNameById.set(g.id || (g as any)._id || '', g.name);
        for (const t of allTeams) {
            // Fallback to (t as any)._id covers legacy raw-mongo responses
            // that haven't been normalised — Team.id is the canonical field.
            const tid = t.id || (t as any)._id;
            const gid = t.groupId;
            if (tid && gid && groupNameById.has(gid)) {
                m.set(tid, groupNameById.get(gid) as string);
            }
        }
        return m;
    }, [allTeams, allGroups]);

    const expandedFilters: FilterField[] = useMemo(() => [
        {
            key: 'activity',
            label: 'Activity',
            type: 'select',
            options: [
                { label: 'Any', value: '' },
                { label: 'Active Today', value: 'today' },
                { label: 'Active Last 7 Days', value: '7days' },
                { label: 'Active Last 30 Days', value: '30days' },
                { label: 'Inactive Today', value: 'inactive-today' },
                { label: 'Inactive Last 7 Days', value: 'inactive-7days' },
                { label: 'Inactive Last 30 Days', value: 'inactive-30days' },
            ],
        },
        {
            key: 'groupIds',
            label: 'Group',
            type: 'grouped-multiselect',
            placeholder: 'All groups',
            options: allGroups.map((g) => ({
                label: g.name || '',
                // Mirror the (g as any)._id fallback alerts-filters uses —
                // legacy raw-mongo responses haven't always been normalised
                // to `id`. Belt-and-braces so the dropdown isn't silently
                // populated with empty values for some payloads.
                value: g.id || (g as any)._id || '',
            })),
        },
        {
            key: 'teamIds',
            label: 'Team',
            type: 'grouped-multiselect',
            // When a group is selected the list is already narrowed, so the
            // group subheader adds noise — omit `group` in that case.
            placeholder: values.groupIds.length > 0 ? 'Teams in selected group(s)' : 'All teams',
            options: visibleTeams.map((t) => {
                const tid = t.id || (t as any)._id || '';
                return {
                    label: t.name || '',
                    value: tid,
                    group: values.groupIds.length === 0 ? groupNameByTeamId.get(tid) : undefined,
                };
            }),
        },
        {
            key: 'tags',
            label: 'User Tags',
            type: 'tag-picker',
            placeholder: 'User tags…',
            // TODO: counts unavailable without a dedicated endpoint; hidden by showing 0
            tagOptions: allTags.map((t) => ({
                id: t._id || '',
                name: t.name,
            })),
        },
    ], [allGroups, visibleTeams, allTags, groupNameByTeamId, values.groupIds]);

    const handleChange = useCallback((key: string, value: any) => {
        setValues((prev) => {
            const next = { ...prev, [key]: value };
            // When the group filter changes, drop any selected teams that are
            // no longer in the narrowed team list so stale chips don't linger.
            if (key === 'groupIds') {
                const selectedGroups = value as string[];
                if (selectedGroups.length > 0) {
                    const groupSet = new Set(selectedGroups);
                    const visibleTeamIds = new Set(
                        allTeams
                            .filter((t) => t.groupId && groupSet.has(t.groupId))
                            .map((t) => t.id || (t as any)._id)
                    );
                    next.teamIds = prev.teamIds.filter((tid) => visibleTeamIds.has(tid));
                }
                // Groups cleared → leave existing teamIds intact (they remain valid)
            }
            return next;
        });
    }, [allTeams]);

    const handleApply = useCallback(() => {
        setApplied(values);
        onFilter(values as UserTypes.Filters).catch((err) => {
            console.error('[UserListFilters] onFilter failed', err);
        });
    }, [values, onFilter]);

    const handleClear = useCallback(() => {
        const cleared = buildEmpty();
        setValues(cleared);
        setApplied(cleared);
        onFilter(cleared as UserTypes.Filters).catch((err) => {
            console.error('[UserListFilters] onFilter (clear) failed', err);
        });
    }, [onFilter]);

    // Remove a single applied filter — clear that key in both values + applied
    // and re-apply immediately so the list updates without another click.
    const removeApplied = useCallback((next: Values) => {
        setValues(next);
        setApplied(next);
        onFilter(next as UserTypes.Filters).catch((err) => {
            console.error('[UserListFilters] onFilter (remove pill) failed', err);
        });
    }, [onFilter]);

    // Team names come from both stores: GroupService.fetchAll overwrites the
    // teams slice with only its group-nested teams, so whichever fetch landed
    // last decides what `allTeams` holds. Reading group-nested teams too makes
    // the pill label independent of that race.
    const teamNameById = useMemo(() => {
        const m = new Map<string, string>();
        const add = (t: any) => {
            const id = t?.id || t?._id;
            // Gate on the name, not the key: a nameless entry in the first source
            // must not block a named copy of the same team in the second.
            if (id && t?.name && !m.get(id)) m.set(id, t.name);
        };
        for (const t of allTeams) add(t);
        for (const g of allGroups) for (const t of (g.teams || [])) add(t);
        return m;
    }, [allTeams, allGroups]);

    const groupNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const g of allGroups) m.set(g.id || (g as any)._id || '', g.name);
        return m;
    }, [allGroups]);

    const tagNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const t of allTags) m.set(t._id || '', t.name);
        return m;
    }, [allTags]);

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        if (applied.name) pills.push({
            key: 'name',
            label: `Name: ${applied.name}`,
            onRemove: () => removeApplied({ ...applied, name: '' }),
        });
        if (applied.phoneNumber) pills.push({
            key: 'phoneNumber',
            label: `Phone: ${applied.phoneNumber}`,
            onRemove: () => removeApplied({ ...applied, phoneNumber: '' }),
        });
        if (applied.employeeId) pills.push({
            key: 'employeeId',
            label: `Employee ID: ${applied.employeeId}`,
            onRemove: () => removeApplied({ ...applied, employeeId: '' }),
        });
        if (applied.activity) pills.push({
            key: 'activity',
            label: `Activity: ${ACTIVITY_LABELS[applied.activity] ?? applied.activity}`,
            onRemove: () => removeApplied({ ...applied, activity: '' }),
        });
        for (const groupId of applied.groupIds) {
            const name = displayName(groupNameById.get(groupId));
            pills.push({
                key: `group:${groupId}`,
                label: `Group: ${name}`,
                onRemove: () => removeApplied({ ...applied, groupIds: applied.groupIds.filter((g) => g !== groupId) }),
            });
        }
        for (const teamId of applied.teamIds) {
            const name = displayName(teamNameById.get(teamId));
            pills.push({
                key: `team:${teamId}`,
                label: `Team: ${name}`,
                onRemove: () => removeApplied({ ...applied, teamIds: applied.teamIds.filter((t) => t !== teamId) }),
            });
        }
        for (const tagId of applied.tags) {
            const name = displayName(tagNameById.get(tagId));
            pills.push({
                key: `tag:${tagId}`,
                label: `Tag: ${name}`,
                onRemove: () => removeApplied({ ...applied, tags: applied.tags.filter((t) => t !== tagId) }),
            });
        }
        return pills;
    }, [applied, teamNameById, groupNameById, tagNameById, removeApplied]);

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            expandedFilters={expandedFilters}
            values={values}
            appliedFilters={appliedFilters}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={onDownload}
        />
    );
};
